import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, X } from "lucide-react";
import { bookingRequest } from "./bookingApi";

// Common IANA zones for the product's stated US/Canada market - not the
// full ~600-zone list, which would need a searchable combobox for little
// added value here.
const TIME_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "America/Winnipeg",
  "America/Halifax",
  "UTC",
];

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const emptyDays = () =>
  Object.fromEntries(
    WEEKDAYS.map((day) => [day, { enabled: false, windows: [{ start: "09:00", end: "17:00" }] }])
  );

const EMPTY_FORM = { full_name: "", email: "", title: "", time_zone: "America/New_York", active: true, days: emptyDays() };

const ProvidersTab = ({ basePath, token }) => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bookingRequest(basePath, token, "GET", "/providers", {
        params: { active_only: showInactive ? "false" : "true" },
      });
      setProviders(data.items || []);
    } catch (err) {
      toast.error(err.message || "Failed to load providers");
    } finally {
      setLoading(false);
    }
  }, [basePath, token, showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (provider) => {
    const days = emptyDays();
    const workingHours = provider.working_hours || {};
    for (const day of WEEKDAYS) {
      const windows = workingHours[day];
      if (windows && windows.length) {
        // Every window this provider actually has, not just the first -
        // this used to silently drop windows[1:] on save.
        days[day] = { enabled: true, windows: windows.map((w) => ({ start: w.start, end: w.end })) };
      }
    }
    setEditingId(provider.id);
    setForm({
      full_name: provider.full_name || "",
      email: provider.email || "",
      title: provider.title || "",
      time_zone: provider.time_zone || "America/New_York",
      active: provider.active !== false,
      days,
    });
    setFormError("");
    setDialogOpen(true);
  };

  const setDay = (day, patch) => {
    setForm((prev) => ({ ...prev, days: { ...prev.days, [day]: { ...prev.days[day], ...patch } } }));
  };

  const setWindow = (day, index, patch) => {
    setForm((prev) => {
      const windows = prev.days[day].windows.map((w, i) => (i === index ? { ...w, ...patch } : w));
      return { ...prev, days: { ...prev.days, [day]: { ...prev.days[day], windows } } };
    });
  };

  const addWindow = (day) => {
    setForm((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: { ...prev.days[day], windows: [...prev.days[day].windows, { start: "09:00", end: "17:00" }] },
      },
    }));
  };

  const removeWindow = (day, index) => {
    setForm((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: { ...prev.days[day], windows: prev.days[day].windows.filter((_, i) => i !== index) },
      },
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    const working_hours = {};
    for (const day of WEEKDAYS) {
      const config = form.days[day];
      if (!config.enabled) continue;

      for (const w of config.windows) {
        if (w.start >= w.end) {
          setFormError(`${day}: end time must be after start time`);
          return;
        }
      }
      // Mirror the backend's own overlap rule (ProviderCreate's
      // _known_weekdays validator): sort by start, reject only a genuine
      // overlap - adjacent windows (one ending exactly when the next
      // starts) are fine.
      const ordered = [...config.windows].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
      for (let i = 0; i < ordered.length - 1; i++) {
        if (ordered[i].end > ordered[i + 1].start) {
          setFormError(
            `${day}: overlapping windows (${ordered[i].start}-${ordered[i].end} and ${ordered[i + 1].start}-${ordered[i + 1].end})`
          );
          return;
        }
      }
      working_hours[day] = ordered.map((w) => ({ start: w.start, end: w.end }));
    }
    setFormError("");
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email || null,
        title: form.title,
        time_zone: form.time_zone,
        active: form.active,
        working_hours,
      };
      if (editingId) {
        await bookingRequest(basePath, token, "PUT", `/providers/${editingId}`, { body: payload });
        toast.success("Provider updated");
      } else {
        await bookingRequest(basePath, token, "POST", "/providers", { body: payload });
        toast.success("Provider added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      setFormError(err.message || "Could not save provider");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="glass-panel border-white/10" data-testid="providers-tab">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <label className="flex items-center gap-2 text-sm text-white/70">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} />
            Show inactive providers
          </label>
          <Button className="primary-btn" onClick={openCreate} data-testid="add-provider-btn">
            <Plus size={16} />
            Add Provider
          </Button>
        </div>

        {loading ? (
          <p className="text-white/60 text-sm">Loading providers...</p>
        ) : providers.length === 0 ? (
          <p className="text-white/60 text-sm py-8 text-center">No providers set up for this clinic yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-white/60">Provider</TableHead>
                <TableHead className="text-white/60">Title</TableHead>
                <TableHead className="text-white/60">Time zone</TableHead>
                <TableHead className="text-white/60">Status</TableHead>
                <TableHead className="text-white/60 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {providers.map((provider) => (
                <TableRow key={provider.id} data-testid={`provider-row-${provider.id}`}>
                  <TableCell className="text-white/90">{provider.full_name}</TableCell>
                  <TableCell className="text-white/70">{provider.title || "—"}</TableCell>
                  <TableCell className="text-white/70">{provider.time_zone}</TableCell>
                  <TableCell className="text-white/70">{provider.active ? "Active" : "Inactive"}</TableCell>
                  <TableCell className="text-right">
                    <Button className="ghost-btn text-xs" onClick={() => openEdit(provider)} data-testid={`edit-provider-${provider.id}`}>
                      <Pencil size={14} /> Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingId ? "Edit Provider" : "Add Provider"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70">Full name</Label>
                <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. RN, Aesthetician" className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Time zone</Label>
                <Select value={form.time_zone} onValueChange={(v) => setForm({ ...form, time_zone: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_ZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Working hours</Label>
              <div className="space-y-3">
                {WEEKDAYS.map((day) => (
                  <div key={day} className="flex items-start gap-3">
                    <Switch
                      checked={form.days[day].enabled}
                      onCheckedChange={(v) => setDay(day, { enabled: v })}
                      className="mt-2"
                    />
                    <span className="text-white/70 text-sm capitalize w-24 mt-2">{day}</span>
                    <div className="flex-1 space-y-2">
                      {form.days[day].windows.map((window, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            type="time"
                            disabled={!form.days[day].enabled}
                            value={window.start}
                            onChange={(e) => setWindow(day, idx, { start: e.target.value })}
                            className="bg-white/5 border-white/10 text-white w-32"
                          />
                          <span className="text-white/40 text-sm">to</span>
                          <Input
                            type="time"
                            disabled={!form.days[day].enabled}
                            value={window.end}
                            onChange={(e) => setWindow(day, idx, { end: e.target.value })}
                            className="bg-white/5 border-white/10 text-white w-32"
                          />
                          {form.days[day].windows.length > 1 && (
                            <Button
                              type="button"
                              className="ghost-btn text-xs px-2"
                              disabled={!form.days[day].enabled}
                              onClick={() => removeWindow(day, idx)}
                              data-testid={`remove-window-${day}-${idx}`}
                            >
                              <X size={14} />
                            </Button>
                          )}
                        </div>
                      ))}
                      {form.days[day].enabled && (
                        <Button
                          type="button"
                          className="ghost-btn text-xs"
                          onClick={() => addWindow(day)}
                          data-testid={`add-window-${day}`}
                        >
                          <Plus size={14} /> Add window
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-white/70">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              Active (bookable)
            </label>
            {formError && <div className="form-error">{formError}</div>}
            <DialogFooter>
              <Button type="button" className="ghost-btn" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="primary-btn" disabled={saving} data-testid="save-provider-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ProvidersTab;
